import type { Vec2 } from './types.ts';

export const PIXELS_PER_METER = 10;
export const BOAT_LENGTH_M = 7;
export const GRID_BASE_M = 10;

export function metersToPixels(meters: number): number {
  return meters * PIXELS_PER_METER;
}

export function pixelsToMeters(pixels: number): number {
  return pixels / PIXELS_PER_METER;
}

/** Hull centre to bow, along heading. Move-line chrome starts here, not at the origin. */
export const BOW_OFFSET_PX = metersToPixels(BOAT_LENGTH_M / 2);

export const MOVEMENT_RADIUS = metersToPixels(30);
export const MOVEMENT_RADIUS_MIN = metersToPixels(2);
export const MOVEMENT_RADIUS_MAX = metersToPixels(30);
export const MOVEMENT_RADIUS_STEP = metersToPixels(1);
export const MIN_SPEED = 0;
export const INITIAL_SPEED = 0;

/** Dock-speed head-on keep for a solid crash (see `crashOutgoingSpeed`). */
export const CRASH_RESTITUTION = 0.25;
/** High-speed floor for that keep; faster rams crumple toward this. */
export const CRASH_RESTITUTION_MIN = 0.1;
/**
 * Coulomb cap for a hull-to-hull scrape, and the slow-speed shore scrape.
 * Faster shore hits shed more of the along-shore run (`shoreKeepFraction`).
 */
export const CRASH_FRICTION = 0.15;
/** Normal-speed scale for crumple, in px/turn (30 m / 4 s ≈ 27 km/h). */
export const CRASH_SPEED_REF = metersToPixels(30);
/** Multiplier on designed crash loss. 0 is clip-only; 1 is the authored curve. */
export const CRASH_HARSHNESS = 1;
export const CRASH_HARSHNESS_MIN = 0;
export const CRASH_HARSHNESS_MAX = 2;
export const CRASH_HARSHNESS_STEP = 0.1;
/**
 * How much of the tightness penalty to apply to kept speed. 0 is the chord
 * (no slingshot). 1 lerps to `chord × sinc(offset)`.
 */
export const TURN_TAX = 0;
export const TURN_TAX_MIN = 0;
export const TURN_TAX_MAX = 1;
export const TURN_TAX_STEP = 0.01;
/** Outbound casts after the first solid hit, then sit at the last shore. */
export const CRASH_BOUNCE_MAX = 4;
/** Water-side turn radius at each bounce; shrinks if leftover is smaller. */
export const CRASH_FILLET_R = metersToPixels(2);
/**
 * How far the rounded bounce may miss the shore it just hit. Keeps the turn
 * against the wall: a reversal too sharp to round inside this stays a snap
 * rather than a curl out in open water.
 */
export const CRASH_FILLET_MISS_PX = 3;
/** Leftover below this stays on the shore instead of casting another outbound. */
export const CRASH_BOUNCE_MIN_PX = 2;

/** How many completed move paths the debug trail keeps. */
export const TRAIL_LIMIT = 24;

/** Metres of wake kept behind the stern; the play HUD stepper writes this. */
export const TRAIL_LENGTH_MIN_M = 5;
export const TRAIL_LENGTH_MAX_M = 1000;
export const TRAIL_LENGTH_STEP_M = 5;
export const TRAIL_LENGTH_DEFAULT_M = 25;

/** How hard last-turn speed stretches the kept wake: `base + gain × distance`. */
export const TRAIL_SPEED_GAIN_MIN = 0;
export const TRAIL_SPEED_GAIN_MAX = 5;
export const TRAIL_SPEED_GAIN_STEP = 0.1;
export const TRAIL_SPEED_GAIN_DEFAULT = 1.5;

export function trailSpeedBonusM(speedGain: number, speedPx: number): number {
  return Math.max(0, speedGain) * pixelsToMeters(Math.max(0, speedPx));
}

export function trailKeepLengthM(
  baseM: number,
  speedGain: number,
  speedPx: number,
): number {
  return baseM + trailSpeedBonusM(speedGain, speedPx);
}

/** Display-only. One committed move is four seconds of boat time. */
export const TURN_DURATION_S = 4;

export function toDisplayKmh(pixelSpeed: number): number {
  return (pixelSpeed / PIXELS_PER_METER / TURN_DURATION_S) * 3.6;
}

/** Inverse of `toDisplayKmh`. km/h into pixels traveled in one turn. */
export function fromDisplayKmh(kmh: number): number {
  return (kmh / 3.6) * PIXELS_PER_METER * TURN_DURATION_S;
}

/** Display-only. Convert metres-per-turn into metres per second (a turn is four seconds). */
export function toDisplayMs(metersPerTurn: number): number {
  return metersPerTurn / TURN_DURATION_S;
}

/** Display-only. Engine heading 0 points right; compass 000 points up. */
export function toCompassBearing(heading: number): number {
  const degrees = (heading * 180) / Math.PI + 90;

  return ((degrees % 360) + 360) % 360;
}

export const INITIAL_HEADING = 0;
export const INITIAL_POSITION: Vec2 = { x: 360, y: 360 };

/**
 * Screen-pixel depth of the catch zone outside the movement rim for sticky-edge aiming.
 * Deliberately uncapped while the movement loop is being felt out: the debug field takes any
 * non-negative number so an absurd halo can be tried without a rebuild.
 */
export const STICK_OVERFLOW_PX = 10;
export const STICK_OVERFLOW_PX_MIN = 0;
export const STICK_OVERFLOW_PX_STEP = 10;
